"use client";

import React, { useState } from "react";
import LiteYouTube from "@/components/LiteYouTube";

type Chapter = { time: string; title: string };

const VOICE_CHANGER_CHAPTERS: Chapter[] = [
  { time: "0:00", title: "Intro: what's new in the voice changer" },
  { time: "0:14", title: "The silence problem with RVC models (and why it mattered)" },
  { time: "0:44", title: "The new patch: silence handling and normalization fix" },
  { time: "1:00", title: "Before and after demo: converting to Freddie Mercury's voice" },
  { time: "1:14", title: "Why you no longer need to manually clip audio" },
  { time: "1:35", title: "How to run a conversion: upload or record" },
  { time: "1:46", title: "Picking a voice with tags (names, genres, eras)" },
  { time: "2:18", title: "Training your own custom models" },
  { time: "2:33", title: "Prep tips: isolate vocals, remove reverb and delay" },
  { time: "3:00", title: "Data augmentation for short audio clips" },
  { time: "3:16", title: "When to use the voice changer (real use cases)" },
];

const EMBED_ID = "y-trbFZJ4qI";
const IFRAME_TITLE = "ChangeLyric Voice Changer Walkthrough: Convert and Train Vocals";

function VideoAndChapters() {
  return (
    <>
      <LiteYouTube videoId={EMBED_ID} title={IFRAME_TITLE} className="max-w-3xl mx-auto mb-4" />
      <div className="max-w-3xl mx-auto">
        <h3 className="text-xs font-bold uppercase tracking-wider text-base-content/50 mb-2">
          Chapters
        </h3>
        <ul className="space-y-1 text-sm text-base-content/80">
          {VOICE_CHANGER_CHAPTERS.map((chapter) => (
            <li key={chapter.time} className="flex gap-3">
              <span className="font-mono text-primary shrink-0 w-12">{chapter.time}</span>
              <span>{chapter.title}</span>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

/**
 * Voice Changer walkthrough video + chapters.
 * - collapsible (default): renders a collapsed card with a +/- toggle, for dropping
 *   onto dashboard/tool pages without taking up space until opened.
 * - collapsible={false}: renders the video + chapters inline, for use inside blog posts.
 */
export default function VoiceChangerTutorial({
  collapsible = true,
  defaultOpen = false,
}: {
  collapsible?: boolean;
  defaultOpen?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultOpen);

  if (!collapsible) {
    return (
      <div className="my-8 max-w-3xl mx-auto">
        <VideoAndChapters />
      </div>
    );
  }

  return (
    <div className="card bg-base-100 shadow-lg border border-base-content/5 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-base-200/50 transition-colors"
      >
        <span className="text-sm font-bold uppercase tracking-wider text-base-content/70">
          Voice Changer walkthrough (3 min)
        </span>
        <span className="text-base-content/40 text-xl leading-none">{expanded ? "−" : "+"}</span>
      </button>
      {expanded && (
        <div className="px-4 pb-4 pt-2 border-t border-base-content/5">
          <VideoAndChapters />
        </div>
      )}
    </div>
  );
}
